import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Bar, Line, Cell } from 'recharts';
import { isToday, isYesterday } from 'date-fns';
import { getActionLabel } from '@/lib/dateRangeActions';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { workflowStatusMap } from '@shared/schema/workflowStatusMap.js';
import { getRunnerSizeDisplay, getRunnerSizeTooltip } from '@shared/schema/runnerSizes.js';
import useWorkflowLogsFilters from './lib/useWorkflowLogsFilters';
import selectors from '@/redux/selectors/selectors';
import { param } from '@/redux/selectors/uiSelectors';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import MonitoringChart from '@/screens/monitoring/components/MonitoringChart';
import formatTimestamp from '@/screens/monitoring/lib/formatTimestamp';
import formatTooltipValue from '@/screens/monitoring/lib/formatTooltipValue';
import Skeleton from '@/components/Skeleton';
import Page from '@/components/page/Page';
import DividerV2 from '@/components/v2/DividerV2';
import Tag from '@/components/Tag';
import { FiActivity, IoWarning, FiCalendar, FiTag, LuContainer, RxLightningBolt, FiSearch, FiUser } from '@/components/react-icons/main/index.esm';
import Spinner from '@/components/Spinner';
import bucketWorkflowRuns from './lib/bucketWorkflowRuns';

const WorkflowLogsModalSummary = () => {
  const { getPreparedFilters } = useWorkflowLogsFilters();
  const workflowIdParam = useArgSelector(param, 'workflowId');
  const workflowIdQuery = useArgSelector(selectors.query, 'workflowId');
  const workflowId = workflowIdParam || workflowIdQuery;
  const logs = useSelector(workflowScreenSelectors.activeWorkflowLogs);
  const isLoading = useArgSelector(selectors.isLoading, 'getWorkflowRunsChart');

  const dateRangeFilter = useArgSelector(selectors.pref, `workflowLogsDateRangeFilter-${workflowId}`);
  const snapshotFilter = useArgSelector(selectors.pref, `workflowLogsSnapshotFilter-${workflowId}`);
  const environmentFilter = useArgSelector(selectors.pref, `workflowLogsEnvironmentFilter-${workflowId}`);
  const statusFilter = useArgSelector(selectors.pref, `workflowLogsStatusFilter-${workflowId}`);
  const activityFilter = useArgSelector(selectors.pref, `workflowLogsActivityFilter-${workflowId}`);
  const endUserFilter = useArgSelector(selectors.pref, `workflowLogsEndUserFilter-${workflowId}`);
  const installationFilter = useArgSelector(selectors.pref, `workflowLogsInstallationFilter-${workflowId}`);
  const searchValue = useArgSelector(selectors.query, 'search');

  const workflowLogsMetadataSnapshots = useSelector(selectors.workflowLogsMetadataSnapshots);
  const workflowLogsMetadataEnvironments = useSelector(selectors.workflowLogsMetadataEnvironments);
  const workflowLogsMetadataEndUsers = useSelector(selectors.workflowLogsMetadataEndUsers);
  const workflowLogsMetadataInstallations = useSelector(selectors.workflowLogsMetadataInstallations);
  const workflowSnapshots = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state)) || [];
  const environments = useSelector(selectors.environments) || [];

  const formatDateRange = (dateRange) => {
    if (!dateRange) return null;

    if (dateRange.action) {
      return getActionLabel(dateRange.action);
    }

    if (dateRange.from === null && dateRange.to === null) {
      return 'All time';
    }

    if (!dateRange.from || !dateRange.to) return null;

    const fromDate = new Date(dateRange.from);
    const toDate = new Date(dateRange.to);

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    };

    if (fromDate.toDateString() === toDate.toDateString()) {
      return formatDate(fromDate);
    }

    if (isYesterday(fromDate) && isToday(toDate)) {
      return 'Last 24 hours';
    }

    return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
  };

  const getSnapshotLabels = (snapshotIds) => {
    if (!snapshotIds?.length) return [];

    return snapshotIds.map((id) => {
      if (id === 'draft') return 'Draft';
      const metadataSnapshot = workflowLogsMetadataSnapshots?.find((s) => s.id === id);
      const workflowSnapshot = workflowSnapshots?.find((s) => s.id === id);
      return metadataSnapshot?.label || workflowSnapshot?.version || id;
    });
  };

  const getEnvironmentLabels = (environmentIds) => {
    if (!environmentIds?.length) return [];

    return environmentIds.map((id) => {
      const metadataEnvironment = workflowLogsMetadataEnvironments?.find((e) => e.id === id);
      const environment = environments?.find((e) => e.id === id);
      return metadataEnvironment?.label || environment?.name || id;
    });
  };

  const getStatusLabels = (statusIds) => {
    if (!statusIds?.length) return [];

    return statusIds.map((id) => {
      return workflowStatusMap[id] || id;
    });
  };

  const getActivityLabels = (activityIds) => {
    if (!activityIds?.length) return [];

    return ['Logs with Activity'];
  };

  const getEndUserLabels = (endUserIds) => {
    if (!endUserIds?.length) return [];

    return endUserIds.map((id) => {
      const endUser = workflowLogsMetadataEndUsers?.find((e) => e.id === id);
      return endUser?.label || id;
    });
  };

  const getInstallationLabels = (installationIds) => {
    if (!installationIds?.length) return [];

    return installationIds.map((id) => {
      const installation = workflowLogsMetadataInstallations?.find((e) => e.id === id);
      return installation?.label || id;
    });
  };

  const activeFilters = useMemo(() => {
    const filters = [];

    const dateRangeLabel = formatDateRange(dateRangeFilter);
    if (dateRangeLabel) {
      filters.push({
        type: 'date',
        label: dateRangeLabel,
        icon: FiCalendar
      });
    }

    const snapshotLabels = getSnapshotLabels(snapshotFilter);
    if (snapshotLabels.length > 0) {
      filters.push({
        type: 'snapshot',
        label: `Version - ${snapshotLabels.join(', ')}`,
        icon: FiTag
      });
    }

    const environmentLabels = getEnvironmentLabels(environmentFilter);
    if (environmentLabels.length > 0) {
      filters.push({
        type: 'environment',
        label: `Env - ${environmentLabels.join(', ')}`,
        icon: LuContainer
      });
    }

    const statusLabels = getStatusLabels(statusFilter);
    if (statusLabels.length > 0) {
      filters.push({
        type: 'status',
        label: `Status - ${statusLabels.join(', ')}`,
        icon: RxLightningBolt
      });
    }

    const activityLabels = getActivityLabels(activityFilter);
    const hasActivityFilter = Array.isArray(activityFilter) ? activityFilter.length > 0 : !!activityFilter;
    if (hasActivityFilter) {
      filters.push({
        type: 'activity',
        label: activityLabels.join(', '),
        icon: FiActivity
      });
    }

    const endUserLabels = getEndUserLabels(endUserFilter);
    if (endUserLabels.length > 0) {
      filters.push({
        type: 'endUser',
        label: `End user - ${endUserLabels.join(', ')}`,
        icon: FiUser
      });
    }

    const installationLabels = getInstallationLabels(installationFilter);
    if (installationLabels.length > 0) {
      filters.push({
        type: 'installation',
        label: `Installation - ${installationLabels.join(', ')}`,
        icon: LuContainer
      });
    }

    if (searchValue && searchValue.trim()) {
      filters.push({
        type: 'search',
        label: 'Search applied to results',
        icon: FiSearch
      });
    }

    return filters;
  }, [dateRangeFilter, snapshotFilter, environmentFilter, statusFilter, activityFilter, endUserFilter, installationFilter, searchValue, workflowLogsMetadataSnapshots, workflowLogsMetadataEnvironments, workflowLogsMetadataEndUsers, workflowLogsMetadataInstallations, workflowSnapshots, environments]);

  const baseChartData = useMemo(() => {
    if (!logs.length) return [];
    const bucketedData = bucketWorkflowRuns(logs);

    return bucketedData.buckets.map((item) => ({
      time: formatTimestamp(item.bucketStart.toISOString()),
      timestamp: item.bucketStart.toISOString(),
      successRuns: item.successRuns,
      warningRuns: item.warningRuns,
      errorRuns: item.errorRuns,
      errors: item.totalErrors,
      warnings: 0,
      events: item.totalSuccess,
      bucketIntervalSeconds: bucketedData.bucketIntervalSeconds,
      bucketMinDate: bucketedData.minDate,
      bucketMaxDate: bucketedData.maxDate,
      targetColumns: bucketedData.targetColumns,
      runnerSize: item.runnerSize
    }));
  }, [logs]);

  const runsChartData = useMemo(() => {
    return baseChartData.map(({ time, timestamp, successRuns, warningRuns, errorRuns }) => ({
      time,
      timestamp,
      successRuns,
      warningRuns,
      errorRuns
    }));
  }, [baseChartData]);

  const errorsWarningsChartData = useMemo(() => {
    return baseChartData.map(({ time, timestamp, errors, warnings }) => ({
      time,
      timestamp,
      errors,
      warnings
    }));
  }, [baseChartData]);

  const eventsChartData = useMemo(() => {
    return baseChartData.map(({ time, timestamp, events }) => ({
      time,
      timestamp,
      events
    }));
  }, [baseChartData]);

  const tooltipFormatter = (value, name) => {
    const formatMap = {
      successRuns: 'Successful Runs',
      warningRuns: 'Runs with Warnings',
      errorRuns: 'Failed Runs',
      errors: 'Errors',
      warnings: 'Warnings',
      events: 'Events'
    };
    return formatTooltipValue(value, formatMap[name] || name);
  };

  const calculateBucketBoundaries = (clickedTimestamp) => {
    const bucketConfig = baseChartData.length > 0 ? baseChartData[0] : null;

    if (bucketConfig && bucketConfig.bucketIntervalSeconds) {
      const bucketStartTime = new Date(clickedTimestamp);
      const bucketIntervalSeconds = parseInt(bucketConfig.bucketIntervalSeconds);
      const bucketEndTime = new Date(bucketStartTime.getTime() + bucketIntervalSeconds * 1000);

      return { startTime: bucketStartTime, endTime: bucketEndTime };
    }
  };

  const handleWorkflowRunsChartClick = (data) => {
    if (!data?.activePayload?.length) return;

    const clickedData = data.activePayload[0].payload;
    const clickedEntry = data.activePayload[0];

    const successCount = clickedData.successRuns || 0;
    const warningCount = clickedData.warningRuns || 0;
    const errorCount = clickedData.errorRuns || 0;
    const totalRuns = successCount + warningCount + errorCount;

    if (totalRuns === 0) return;

    let runType = clickedEntry.dataKey;
    const runCount = clickedData[runType] || 0;

    if (runCount === 0) {
      if (errorCount > 0) {
        runType = 'errorRuns';
      } else if (warningCount > 0) {
        runType = 'warningRuns';
      } else if (successCount > 0) {
        runType = 'successRuns';
      }
    }

    const { startTime, endTime } = calculateBucketBoundaries(clickedData.timestamp);

    let modalRunType = runType.replace('Runs', '');
    const activeRunTypes = [successCount > 0, warningCount > 0, errorCount > 0].filter(Boolean).length;

    if (activeRunTypes > 1) {
      modalRunType = 'all';
    }

    setModalState('workflowRunDetails', {
      open: true,
      data: {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        workflowId,
        runType: modalRunType,
        clickedRunType: runType,
        totalCounts: {
          success: successCount,
          warning: warningCount,
          error: errorCount
        },
        filters: getPreparedFilters()
      }
    });
  };

  const getBarRadius = (item, key) => {
    if (!item[key]) return [0, 0, 0, 0];

    const hasError = item.errorRuns > 0;
    const hasWarning = item.warningRuns > 0;
    const hasSuccess = item.successRuns > 0;

    if (key === 'errorRuns' && hasError) return [2, 2, 0, 0];
    if (key === 'warningRuns' && hasWarning && !hasError) return [2, 2, 0, 0];
    if (key === 'successRuns' && hasSuccess && !hasError && !hasWarning) return [2, 2, 0, 0];

    return [0, 0, 0, 0];
  };

  const hasWarnings = useMemo(() => {
    return baseChartData.some((item) => item.warnings > 0);
  }, [baseChartData]);

  const hasEvents = useMemo(() => {
    return baseChartData.some((item) => item.events > 0);
  }, [baseChartData]);

  const totalRuns = useMemo(() => {
    return baseChartData.reduce((total, item) => {
      return total + (item.successRuns || 0) + (item.warningRuns || 0) + (item.errorRuns || 0);
    }, 0);
  }, [baseChartData]);

  const runnerSizeFromChartData = useMemo(() => {
    if (!baseChartData.length) return null;
    return baseChartData[0]?.runnerSize || null;
  }, [baseChartData]);

  const getRunnerSizeDisplayForSummary = () => {
    if (runnerSizeFromChartData) {
      return getRunnerSizeDisplay(runnerSizeFromChartData);
    }

    return 'No data';
  };

  return (
    <div className="workflow-logs-modal-summary">
      <div className="summary-header">
        <h1 className="workflow-title">Logs Summary</h1>
        {activeFilters.length > 0 && (
          <div className="summary-filters">
            <div className="filter-tags">
              {activeFilters.map((filter, index) => (
                <Tag key={`${filter.type}-${index}`} variant={filter.variant} icon={filter.icon} size="sm">
                  {filter.label}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>

      <Page.Section variant="chromeless" className="infos-section">
        <div className="infos-container">
          <div className="log-info">
            <div className="info-label">Assigned Runner Size</div>
            {isLoading && (
              <div className="info-value">
                <Spinner size="sm" />
              </div>
            )}
            {!isLoading && (
              <div className="info-value" data-title={runnerSizeFromChartData ? getRunnerSizeTooltip(runnerSizeFromChartData) : 'No runner size data available'}>
                {getRunnerSizeDisplayForSummary()}
              </div>
            )}
          </div>
          <div className="log-info">
            <div className="info-label">Total Runs</div>
            {isLoading && (
              <div className="info-value">
                <Spinner size="sm" />
              </div>
            )}
            {!isLoading && <div className="info-value">{totalRuns}</div>}
          </div>
        </div>
      </Page.Section>

      {isLoading && (
        <div className="health-charts">
          <Skeleton skeletonStyle={{ height: 340, borderRadius: 6 }} containerStyle={{ padding: 0 }} />
          <Skeleton skeletonStyle={{ height: 170, borderRadius: 6 }} containerStyle={{ padding: 0 }} />
        </div>
      )}

      {!isLoading && (
        <div className="health-charts">
          <div className="health-chart">
            <Page.Section variant="chromeless">
              <MonitoringChart
                data={runsChartData}
                title="Workflow Runs"
                emptyState={{
                  icon: FiActivity,
                  title: 'No workflow runs data available',
                  description: 'Try adjusting your date range or check if there are workflow runs'
                }}
                chartType="bar"
                tooltipFormatter={tooltipFormatter}
                height={80}
                noSection
                onDataPointClick={handleWorkflowRunsChartClick}>
                <Bar dataKey="successRuns" stackId="runs" fill="#4be165" name="Successful Runs" background={{ fill: '#F6F6F6', radius: [2, 2, 0, 0] }}>
                  {runsChartData?.map((item) => (
                    <Cell key={item?.time} fill="#4be165" radius={getBarRadius(item, 'successRuns')} />
                  ))}
                </Bar>
                <Bar dataKey="warningRuns" stackId="runs" fill="#e9a154" name="Runs with Warnings">
                  {runsChartData?.map((item) => (
                    <Cell key={item?.time} fill="#e9a154" radius={getBarRadius(item, 'warningRuns')} />
                  ))}
                </Bar>
                <Bar dataKey="errorRuns" stackId="runs" fill="#ff7575" name="Failed Runs">
                  {runsChartData?.map((item) => (
                    <Cell key={item?.time} fill="#ff7575" radius={getBarRadius(item, 'errorRuns')} />
                  ))}
                </Bar>
              </MonitoringChart>
              <DividerV2 />
              <MonitoringChart
                data={errorsWarningsChartData}
                title="Step Errors & Warnings"
                emptyState={{
                  icon: IoWarning,
                  title: 'No step errors & warnings data available',
                  description: 'Try adjusting your date range or check if there are workflow operation logs'
                }}
                chartType="line"
                tooltipFormatter={tooltipFormatter}
                height={80}
                noSection>
                <Line type="monotone" dataKey="errors" stroke="#ff7575" strokeWidth={2} dot={false} connectNulls={false} name="Errors" />
                {hasWarnings && <Line type="monotone" dataKey="warnings" stroke="#e9a154" strokeWidth={2} dot={false} connectNulls={false} name="Warnings" />}
              </MonitoringChart>
            </Page.Section>
          </div>

          {hasEvents && (
            <div className="health-chart">
              <MonitoringChart
                data={eventsChartData}
                title="Activity"
                emptyState={{
                  icon: FiActivity,
                  title: 'No activity data available',
                  description: 'Try adjusting your date range or check if there are successful operations'
                }}
                chartType="line"
                tooltipFormatter={tooltipFormatter}
                height={80}>
                <Line type="monotone" dataKey="events" stroke="#4be165" strokeWidth={2} dot={false} connectNulls={false} name="Activity (Success Operations)" />
              </MonitoringChart>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkflowLogsModalSummary;
